import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Grid,
} from '@mui/material'
import {
  ArrowBack as BackIcon,
  ExpandMore as ExpandMoreIcon,
  Publish as PublishIcon,
  AccessTime as TimeIcon,
  School as ChapterIcon,
  CheckCircle as ObjectiveIcon,
} from '@mui/icons-material'
import { educationModulesAPI } from '../services/api'

// Status indicator styles
const statusStyles = {
  draft: { color: '#ca8a04', label: 'Draft' },
  published: { color: '#16a34a', label: 'Published' },
  archived: { color: '#71717a', label: 'Archived' },
}

function StatusIndicator({ status }) {
  const style = statusStyles[status] || { color: '#71717a', label: status }
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: style.color,
        }}
      />
      <Typography variant="body2" sx={{ color: style.color, fontSize: '0.8125rem' }}>
        {style.label}
      </Typography>
    </Box>
  )
}

// Lesson type styles
const lessonTypeStyles = {
  lesson: { color: '#0284c7', label: 'Lesson' },
  exercise: { color: '#7c3aed', label: 'Exercise' },
  quiz: { color: '#ca8a04', label: 'Quiz' },
  summary: { color: '#0f766e', label: 'Summary' },
}

function LessonTypeIndicator({ type }) {
  const style = lessonTypeStyles[type] || { color: '#71717a', label: type }
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, minWidth: 80 }}>
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: style.color,
        }}
      />
      <Typography variant="body2" sx={{ color: style.color, fontSize: '0.8125rem' }}>
        {style.label}
      </Typography>
    </Box>
  )
}


function EducationModuleView() {
  const navigate = useNavigate()
  const { moduleId } = useParams()
  const [module, setModule] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [publishing, setPublishing] = useState(false)
  const [expandedChapter, setExpandedChapter] = useState(null)

  const fetchModule = useCallback(async () => {
    if (!moduleId) return
    try {
      setLoading(true)
      const data = await educationModulesAPI.getById(moduleId)
      setModule(data)
      setError(null)
      // Auto-expand first chapter
      if (data.chapters?.length > 0) {
        setExpandedChapter(data.chapters[0].chapter_id)
      }
    } catch (err) {
      console.error('Error fetching module:', err)
      setError(err.response?.data?.error || 'Failed to load module')
    } finally {
      setLoading(false)
    }
  }, [moduleId])

  useEffect(() => {
    fetchModule()
  }, [fetchModule])

  const handlePublish = async () => {
    try {
      setPublishing(true)
      await educationModulesAPI.publish(moduleId)
      await fetchModule()
    } catch (err) {
      setError('Failed to publish module: ' + (err.response?.data?.error || err.message))
    } finally {
      setPublishing(false)
    }
  }

  const handleAccordionChange = (chapterId) => (event, isExpanded) => {
    setExpandedChapter(isExpanded ? chapterId : null)
  }

  const getTotalStats = () => {
    if (!module?.chapters) return { chapters: 0, lessons: 0, minutes: 0 }

    let totalLessons = 0
    let totalMinutes = 0

    for (const chapter of module.chapters) {
      totalLessons += chapter.lessons?.length || 0
      totalMinutes += chapter.estimated_minutes || 0
    }

    return {
      chapters: module.chapters.length,
      lessons: totalLessons,
      minutes: totalMinutes,
    }
  }

  const formatTime = (minutes) => {
    if (!minutes) return '—'
    if (minutes < 60) return `${minutes} min`
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
  }

  const LessonTypeIcon = ({ type }) => {
    const Icon = LESSON_TYPE_ICONS[type] || LessonIcon
    return <Icon fontSize="small" />
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!module) {
    return (
      <Box>
        <Alert severity="error">{error || 'Module not found'}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/education-modules')} sx={{ mt: 2 }}>
          Back to Education Modules
        </Button>
      </Box>
    )
  }

  const stats = getTotalStats()

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3, gap: 2 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/education-modules')}
          variant="outlined"
          sx={{ mt: 0.5 }}
        >
          Back
        </Button>
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography variant="h5">{module.title}</Typography>
            <StatusIndicator status={module.status} />
            <Typography variant="body2" color="text.secondary">
              v{module.version}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" fontFamily="monospace" fontSize="0.75rem">
            {module.module_id}
          </Typography>
        </Box>
        {module.status !== 'published' && (
          <Button
            variant="contained"
            color="success"
            startIcon={publishing ? <CircularProgress size={18} color="inherit" /> : <PublishIcon />}
            onClick={handlePublish}
            disabled={publishing}
          >
            Publish
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Module Info */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="body1" sx={{ mb: 3 }}>
          {module.description}
        </Typography>

        <Divider sx={{ my: 2 }} />

        {/* Stats */}
        <Grid container spacing={3}>
          <Grid item xs={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary">{stats.chapters}</Typography>
              <Typography variant="body2" color="text.secondary">Chapters</Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary">{stats.lessons}</Typography>
              <Typography variant="body2" color="text.secondary">Lessons</Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary">{formatTime(stats.minutes)}</Typography>
              <Typography variant="body2" color="text.secondary">Total Time</Typography>
            </Box>
          </Grid>
        </Grid>

        {module.knowledge_base_id && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              <strong>Dify Knowledge Base:</strong>{' '}
              <code style={{ fontSize: '0.85em' }}>{module.knowledge_base_id}</code>
            </Typography>
          </>
        )}
      </Paper>

      {/* Chapters */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Chapters & Lessons
      </Typography>

      {module.chapters?.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">No chapters in this module</Typography>
        </Paper>
      ) : (
        module.chapters?.map((chapter) => (
          <Accordion
            key={chapter.chapter_id}
            expanded={expandedChapter === chapter.chapter_id}
            onChange={handleAccordionChange(chapter.chapter_id)}
            sx={{ mb: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                <ChapterIcon color="primary" />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    Chapter {chapter.chapter_number}: {chapter.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {chapter.description}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {formatTime(chapter.estimated_minutes)}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {chapter.lessons?.length || 0} lessons
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <List disablePadding>
                {chapter.lessons?.map((lesson, index) => (
                  <React.Fragment key={lesson.lesson_id}>
                    {index > 0 && <Divider component="li" />}
                    <ListItem alignItems="flex-start" sx={{ py: 2 }}>
                      <ListItemIcon sx={{ mt: 0.5, minWidth: 100 }}>
                        <LessonTypeIndicator type={lesson.lesson_type} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2">
                              {chapter.chapter_number}.{lesson.lesson_number} {lesson.title}
                            </Typography>
                            {lesson.exercise_type && (
                              <Typography variant="caption" color="text.secondary">
                                ({lesson.exercise_type})
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                              {lesson.estimated_minutes} min
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {lesson.description}
                            </Typography>
                            {lesson.learning_objectives?.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                  Learning Objectives:
                                </Typography>
                                <List dense disablePadding sx={{ mt: 0.5 }}>
                                  {lesson.learning_objectives.map((obj, i) => (
                                    <ListItem key={i} disableGutters sx={{ py: 0.25 }}>
                                      <ListItemIcon sx={{ minWidth: 24 }}>
                                        <ObjectiveIcon sx={{ fontSize: 14 }} color="success" />
                                      </ListItemIcon>
                                      <ListItemText
                                        primary={obj}
                                        primaryTypographyProps={{ variant: 'caption' }}
                                      />
                                    </ListItem>
                                  ))}
                                </List>
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </Box>
  )
}

export default EducationModuleView
